import type { Reducer } from 'redux';
import { Actions } from '../actionTypes/actionTypes';
import { getInitialActiveDealState } from './activeDealInitialState';
import { bootstrapActionTypes, initActionTypes } from '@makemydeal/dr-shared-store';
import { ApiRequestStatus } from '@makemydeal/dr-shared-types';
import type { ActiveDealState } from './types';

export const activeDeal: Reducer<ActiveDealState> = (state: ActiveDealState = { ...getInitialActiveDealState() }, action: any) => {
    const { payload, type, meta } = action;

    switch (type) {
        case bootstrapActionTypes.BOOTSTRAP_INITIAL_LOAD: {
            return {
                ...state,
                isInitializing: true
            };
        }

        case initActionTypes.INIT_PENCIL_SUCCESS: {
            // save deal response
            if (payload.deal) {
                const { details } = payload.deal;
                return {
                    ...state,
                    details: details,
                    isInitializing: false,
                    applicationState: payload.applicationState
                };
            }
            // is invalid deal
            return {
                ...state,
                isInitializing: false
            };
        }

        case Actions.UPDATE_DEAL_REQUEST: {
            return {
                ...state,
                updateDealStatus: ApiRequestStatus.Loading,
                changeType: meta?.changeType
            };
        }

        // BFF returns the updated deal in the response, use it directly as the source of truth.
        case Actions.UPDATE_DEAL_SUCCESS: {
            const saved = { ...state, updateDealStatus: ApiRequestStatus.Success };

            if (!state.details) return saved;

            if (payload?.data?.deal) {
                // BFF returns { deal: { details } }
                const { details } = payload.data.deal;
                if (details) {
                    return {
                        ...saved,
                        details
                    };
                }
                // Fallback: old response format (deal is the DxgDeal directly)
                return {
                    ...saved,
                    details: payload.data.deal
                };
            }

            return saved;
        }

        case Actions.UPDATE_DEAL_FAILURE: {
            return {
                ...state,
                updateDealStatus: ApiRequestStatus.Error
            };
        }

        case Actions.FETCH_DEAL_SUCCESS: {
            if (!state.details) return state;

            if (payload?.data?.deal) {
                return {
                    ...state,
                    details: payload.data.deal,
                    applicationState: payload.data.applicationState
                };
            }

            return state;
        }

        case Actions.SET_ACTIVE_DEAL_CHANGE_TYPE: {
            return {
                ...state,
                changeType: payload?.changeType
            };
        }

        case Actions.SET_DEAL_ACCEPTED_BY_BUYER: {
            if (!state.details) return state;

            return {
                ...state,
                details: {
                    ...state.details,
                    dealStructure: {
                        ...state.details.dealStructure,
                        acceptedByBuyer: payload?.acceptedByBuyer
                    }
                }
            };
        }

        case Actions.UPDATE_ACTIVE_DEAL_VISIBILITY_STATUS: {
            if (!state.details) return state;

            if (payload?.visibility) {
                return {
                    ...state,
                    details: {
                        ...state.details,
                        dealStructure: {
                            ...state.details.dealStructure,
                            visibility: payload.visibility
                        }
                    }
                };
            }

            return state;
        }

        case Actions.UPDATE_DEALREFID: {
            if (!state.details) return state;

            if (payload?.dealRefId) {
                return {
                    ...state,
                    details: {
                        ...state.details,
                        coxAutoReferences: {
                            ...state.details.coxAutoReferences,
                            dealRefId: payload.dealRefId
                        }
                    }
                };
            }
            return state;
        }

        // Merges the deal a participants save returned over the one in state, so anything the save
        // omits keeps its current value.
        case Actions.UPDATE_ACTIVE_DEAL_FROM_PARTICIPANTS_SAVE: {
            if (!state.details || !payload?.dxgDeal?.dealStructure) return state;

            return {
                ...state,
                details: {
                    ...state.details,
                    ...payload.dxgDeal
                }
            };
        }

        case Actions.TRADE_IN_UPDATE_SUCCESS:
        case Actions.TRADE_IN_REMOVE_SUCCESS: {
            if (!state.details) return state;

            if (payload?.data?.deal?.details) {
                return {
                    ...state,
                    applicationState: payload.data.applicationState,
                    details: payload.data.deal.details
                };
            }
            return state;
        }

        default: {
            return state;
        }
    }
};
